import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import ProtectedRoute from './components/auth/ProtectedRoute';
import ErrorBoundary from './components/common/ErrorBoundary';
import Login from './components/auth/Login';
import RequestPasswordReset from './components/auth/RequestPasswordReset';
import ResetPassword from './components/auth/ResetPassword';
import VerifyEmail from './components/auth/VerifyEmail';
import AcceptInvitation from './components/auth/AcceptInvitation';
import Navbar from './components/Navbar';
import Dashboard from './pages/Dashboard';
import Calendar from './pages/Calendar';
import CalendarSettings from './pages/CalendarSettings';
import Cases from './pages/Cases';
import Clients from './pages/Clients';
import Users from './pages/Users';
import Firms from './pages/Firms';
import Migrations from './pages/Migrations';
import MarketingStrategy from './pages/MarketingStrategy';
import TemplateLibrary from './components/TemplateLibrary';
import SettingsPage from './pages/settings/SettingsPage';
import SchedulingSettings from './components/SchedulingSettings';
import { CaseDetail, ProjectDetail, TaskDetail, ClientDetail } from './pages/details';
import Persons from './pages/Persons';
import PersonDetail from './pages/details/PersonDetail';
import TrustAccounts from './pages/TrustAccounts';
import TrustAccountDetail from './pages/details/TrustAccountDetail';
import OperatingAccounts from './pages/OperatingAccounts';
import OperatingAccountDetail from './pages/details/OperatingAccountDetail';
import Billing from './pages/Billing';
import ClientTransactionHistory from './pages/ClientTransactionHistory';
import Invoices from './pages/Invoices';
import InvoiceDetail from './pages/details/InvoiceDetail';
import Communications from './pages/Communications';
import Reports from './pages/Reports';
import Feedback from './pages/Feedback';
import FeedbackDetail from './pages/FeedbackDetail';
import Goals from './pages/Goals';
import TimeEntries from './pages/TimeEntries';
import CommandPalette from './components/CommandPalette';
import SigningEnvelopes from './pages/SigningEnvelopes';
import Tools from './pages/Tools';
import DocumentTemplateHelp from './pages/DocumentTemplateHelp';
import DocumentsAndTemplates from './pages/DocumentsAndTemplates';
import ObservabilityDashboard from './pages/admin/ObservabilityDashboard';
import DevIssueBoard from './pages/admin/DevIssueBoard';
import RedactionEditorPage from './pages/RedactionEditorPage';
import Tasks from './pages/Tasks';
import CaseDocumentsPage from './pages/CaseDocumentsPage';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import WhyJustPractice from './pages/WhyJustPractice';
import Pricing from './pages/Pricing';
import Register from './pages/Register';
import PublicPayment from './pages/PublicPayment';
import SystemAlertBanner from './components/common/SystemAlertBanner';
import PageLayout from './components/common/PageLayout';
import SupportChatWidget from './components/support-chat/SupportChatWidget';
import { NavbarStatusProvider } from './contexts/NavbarStatusContext';
import { PortalAuthProvider } from './contexts/PortalAuthContext';
import PortalProtectedRoute from './components/portal/PortalProtectedRoute';
import PortalLogin from './components/portal/PortalLogin';
import AcceptPortalInvitation from './components/portal/AcceptPortalInvitation';
import PortalLayout from './pages/portal/PortalLayout';
import PortalDashboard from './pages/portal/PortalDashboard';
import PortalCaseDetail from './pages/portal/PortalCaseDetail';
import PortalInvoices from './pages/portal/PortalInvoices';
import PortalInvoiceDetail from './pages/portal/PortalInvoiceDetail';
import PortalMessages from './pages/portal/PortalMessages';
import PortalMessageThread from './pages/portal/PortalMessageThread';
import PortalEvents from './pages/portal/PortalEvents';
import PortalTasks from './pages/portal/PortalTasks';
import PortalDocumentRequests from './pages/portal/PortalDocumentRequests';
import PortalDocuments from './pages/portal/PortalDocuments';
import PortalProfile from './pages/portal/PortalProfile';
import './App.css';

// Component to handle Ctrl+K command palette and navbar search icon
function CommandPaletteHandler() {
  const [isOpen, setIsOpen] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    if (!user) return;

    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setIsOpen(true);
      }
    };

    const handleOpenEvent = () => setIsOpen(true);

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('open-command-palette', handleOpenEvent);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('open-command-palette', handleOpenEvent);
    };
  }, [user]);

  if (!user) return null;

  return (
    <CommandPalette isOpen={isOpen} onClose={() => setIsOpen(false)} />
  );
}

function HomeRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-yellow-500"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <WhyJustPractice />;
  }

  return (
    <ProtectedRoute>
      {children}
    </ProtectedRoute>
  );
}

function App() {
  return (
    <AuthProvider>
      <NavbarStatusProvider>
      <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <div className="App min-h-screen bg-gray-50">
          <CommandPaletteHandler />
          <SupportChatWidget />
          <SystemAlertBanner />
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/request-password-reset" element={<RequestPasswordReset />} />
            <Route path="/reset-password/:token" element={<ResetPassword />} />
            <Route path="/verify-email/:token" element={<VerifyEmail />} />
            <Route path="/accept-invitation/:token" element={<AcceptInvitation />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
            <Route path="/why" element={<WhyJustPractice />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/register" element={<Register />} />
            <Route path="/pay/:token" element={<PublicPayment />} />

            {/* Home route - login page or dashboard (no redirect, so Google verification sees a real page at /) */}
            <Route path="/" element={
              <HomeRoute>
                <Navbar />
                <PageLayout>
                  <Dashboard />
                </PageLayout>
              </HomeRoute>
            } />

            <Route path="/dashboard" element={
              <ProtectedRoute>
                <Navbar />
                <PageLayout>
                  <Dashboard />
                </PageLayout>
              </ProtectedRoute>
            } />
            
            <Route path="/calendar" element={
              <ProtectedRoute>
                <Navbar />
                <PageLayout>
                  <Calendar />
                </PageLayout>
              </ProtectedRoute>
            } />
            
            <Route path="/calendar/settings" element={
              <ProtectedRoute>
                <Navbar />
                <PageLayout>
                  <CalendarSettings />
                </PageLayout>
              </ProtectedRoute>
            } />
            
            <Route path="/cases" element={
              <ProtectedRoute>
                <Navbar />
                <PageLayout>
                  <Cases />
                </PageLayout>
              </ProtectedRoute>
            } />
            
            <Route path="/cases/:id" element={
              <ProtectedRoute>
                <Navbar />
                <PageLayout>
                  <CaseDetail />
                </PageLayout>
              </ProtectedRoute>
            } />

            <Route path="/cases/:id/documents" element={
              <ProtectedRoute>
                <Navbar />
                <CaseDocumentsPage />
              </ProtectedRoute>
            } />

            <Route path="/projects/:id" element={
              <ProtectedRoute>
                <Navbar />
                <PageLayout>
                  <ProjectDetail />
                </PageLayout>
              </ProtectedRoute>
            } />
            
            <Route path="/tasks" element={
              <ProtectedRoute>
                <Navbar />
                <PageLayout>
                  <Tasks />
                </PageLayout>
              </ProtectedRoute>
            } />

            <Route path="/tasks/:id" element={
              <ProtectedRoute>
                <Navbar />
                <PageLayout>
                  <TaskDetail />
                </PageLayout>
              </ProtectedRoute>
            } />
            
            <Route path="/clients" element={
              <ProtectedRoute>
                <Navbar />
                <PageLayout>
                  <Clients />
                </PageLayout>
              </ProtectedRoute>
            } />

            <Route path="/clients/:id" element={
              <ProtectedRoute>
                <Navbar />
                <PageLayout>
                  <ClientDetail />
                </PageLayout>
              </ProtectedRoute>
            } />

            <Route path="/persons" element={
              <ProtectedRoute>
                <Navbar />
                <PageLayout>
                  <Persons />
                </PageLayout>
              </ProtectedRoute>
            } />

            <Route path="/persons/:id" element={
              <ProtectedRoute>
                <Navbar />
                <PageLayout>
                  <PersonDetail />
                </PageLayout>
              </ProtectedRoute>
            } />

            <Route path="/reports" element={
              <ProtectedRoute>
                <Navbar />
                <PageLayout>
                  <Reports />
                </PageLayout>
              </ProtectedRoute>
            } />

            <Route path="/communications" element={
              <ProtectedRoute>
                <Navbar />
                <PageLayout>
                  <Communications />
                </PageLayout>
              </ProtectedRoute>
            } />
            
            <Route path="/users" element={
              <ProtectedRoute>
                <Navbar />
                <PageLayout>
                  <Users />
                </PageLayout>
              </ProtectedRoute>
            } />

            <Route path="/firms" element={
              <ProtectedRoute>
                <Navbar />
                <PageLayout>
                  <Firms />
                </PageLayout>
              </ProtectedRoute>
            } />

            <Route path="/migrations" element={
              <ProtectedRoute>
                <Navbar />
                <PageLayout>
                  <Migrations />
                </PageLayout>
              </ProtectedRoute>
            } />

            <Route path="/marketing-strategy" element={
              <ProtectedRoute>
                <Navbar />
                <PageLayout>
                  <MarketingStrategy />
                </PageLayout>
              </ProtectedRoute>
            } />

            <Route path="/templates" element={
              <ProtectedRoute>
                <Navbar />
                <PageLayout>
                  <TemplateLibrary />
                </PageLayout>
              </ProtectedRoute>
            } />
            
            <Route path="/settings" element={
              <ProtectedRoute>
                <Navbar />
                <SettingsPage />
              </ProtectedRoute>
            } />
            
            <Route path="/settings/scheduling" element={
              <ProtectedRoute>
                <Navbar />
                <SchedulingSettings />
              </ProtectedRoute>
            } />
            
            <Route path="/trust-accounts" element={
              <ProtectedRoute>
                <Navbar />
                <PageLayout>
                  <TrustAccounts />
                </PageLayout>
              </ProtectedRoute>
            } />
            
            <Route path="/trust-accounts/:accountId" element={
              <ProtectedRoute>
                <Navbar />
                <PageLayout>
                  <TrustAccountDetail />
                </PageLayout>
              </ProtectedRoute>
            } />

            <Route path="/operating-accounts" element={
              <ProtectedRoute>
                <Navbar />
                <PageLayout>
                  <OperatingAccounts />
                </PageLayout>
              </ProtectedRoute>
            } />

            <Route path="/operating-accounts/:accountId" element={
              <ProtectedRoute>
                <Navbar />
                <PageLayout>
                  <OperatingAccountDetail />
                </PageLayout>
              </ProtectedRoute>
            } />

            <Route path="/billing" element={
              <ProtectedRoute>
                <Navbar />
                <ErrorBoundary>
                  <PageLayout>
                    <Billing />
                  </PageLayout>
                </ErrorBoundary>
              </ProtectedRoute>
            } />

            <Route path="/billing/clients/:clientId/transactions" element={
              <ProtectedRoute>
                <Navbar />
                <ErrorBoundary>
                  <PageLayout>
                    <ClientTransactionHistory />
                  </PageLayout>
                </ErrorBoundary>
              </ProtectedRoute>
            } />

            <Route path="/invoices" element={
              <ProtectedRoute>
                <Navbar />
                <PageLayout>
                  <Invoices />
                </PageLayout>
              </ProtectedRoute>
            } />
            <Route path="/invoices/:invoiceId" element={
              <ProtectedRoute>
                <Navbar />
                <PageLayout>
                  <InvoiceDetail />
                </PageLayout>
              </ProtectedRoute>
            } />

            <Route path="/signing" element={
              <ProtectedRoute>
                <Navbar />
                <PageLayout>
                  <SigningEnvelopes />
                </PageLayout>
              </ProtectedRoute>
            } />

            <Route path="/tools" element={
              <ProtectedRoute>
                <Navbar />
                <PageLayout>
                  <Tools />
                </PageLayout>
              </ProtectedRoute>
            } />

            <Route path="/document-templates/help" element={
              <ProtectedRoute>
                <Navbar />
                <PageLayout>
                  <DocumentTemplateHelp />
                </PageLayout>
              </ProtectedRoute>
            } />

            {/* Redirect old doc-templates path to new documents path */}
            <Route path="/document-templates" element={<Navigate to="/documents" replace />} />

            <Route path="/documents" element={
              <ProtectedRoute>
                <Navbar />
                <PageLayout>
                  <DocumentsAndTemplates />
                </PageLayout>
              </ProtectedRoute>
            } />

            <Route path="/feedback" element={
              <ProtectedRoute>
                <Navbar />
                <PageLayout>
                  <Feedback />
                </PageLayout>
              </ProtectedRoute>
            } />

            <Route path="/feedback/:id" element={
              <ProtectedRoute>
                <Navbar />
                <PageLayout>
                  <FeedbackDetail />
                </PageLayout>
              </ProtectedRoute>
            } />

            <Route path="/goals" element={
              <ProtectedRoute>
                <Navbar />
                <PageLayout>
                  <Goals />
                </PageLayout>
              </ProtectedRoute>
            } />

            <Route path="/time-tracking" element={
              <ProtectedRoute>
                <Navbar />
                <PageLayout>
                  <TimeEntries />
                </PageLayout>
              </ProtectedRoute>
            } />

            <Route path="/documents/:id/redact" element={
              <ProtectedRoute>
                <Navbar />
                <RedactionEditorPage />
              </ProtectedRoute>
            } />

            {/* Client Portal routes — single PortalAuthProvider for all portal routes */}
            <Route path="/portal/accept-invitation/:token" element={
              <AcceptPortalInvitation />
            } />
            <Route element={<PortalAuthProvider><Outlet /></PortalAuthProvider>}>
              <Route path="/portal/login" element={<PortalLogin />} />
              <Route path="/portal/*" element={
                <PortalProtectedRoute>
                  <PortalLayout />
                </PortalProtectedRoute>
              }>
              <Route index element={<PortalDashboard />} />
              <Route path="cases/:caseId" element={<PortalCaseDetail />} />
              <Route path="invoices" element={<PortalInvoices />} />
              <Route path="invoices/:invoiceId" element={<PortalInvoiceDetail />} />
              <Route path="messages" element={<PortalMessages />} />
              <Route path="messages/:conversationId" element={<PortalMessageThread />} />
              <Route path="events" element={<PortalEvents />} />
              <Route path="tasks" element={<PortalTasks />} />
              <Route path="document-requests" element={<PortalDocumentRequests />} />
              <Route path="documents" element={<PortalDocuments />} />
              <Route path="profile" element={<PortalProfile />} />
            </Route>
            </Route>

            <Route path="/admin/observability" element={
              <ProtectedRoute>
                <Navbar />
                <ObservabilityDashboard />
              </ProtectedRoute>
            } />

            <Route path="/admin/dev-issues" element={
              <ProtectedRoute>
                <Navbar />
                <PageLayout className="h-[calc(100vh-64px)] flex flex-col overflow-hidden">
                  <DevIssueBoard />
                </PageLayout>
              </ProtectedRoute>
            } />

            {/* Catch all route */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </div>
      </Router>
      </NavbarStatusProvider>
    </AuthProvider>
  );
}

export default App;
